import type { Delta, Metric, MetricKind } from "./types";

/* ---------- Value formatting ---------- */

/** A metric number shown "as is": a whole number prints with no decimals; a
 *  value with a fractional part prints exactly 2 decimals, TRUNCATED — never
 *  rounded. 318 → "318", 8.1332 → "8.13", 85.8571 → "85.85", 318.1 → "318.10".
 *  Thousands separators are kept. */
export function fmtNum(v: number): string {
  // `v * 100` is truncated, so it must first be freed of the error binary
  // floating point introduces: 79.96 * 100 is 7995.999999999999, and truncating
  // THAT prints 79.95 — a value the API never sent. Re-reading the product at 15
  // significant digits (a double carries 15–17) collapses the artifact back onto
  // 7996 while leaving any genuine difference — 79.9599999 is still below 79.96
  // — intact, because a real one is orders of magnitude larger than 1e-15.
  const scaled = Number((v * 100).toPrecision(15));
  const truncated = Math.trunc(scaled) / 100;
  return Number.isInteger(truncated)
    ? truncated.toLocaleString()
    : truncated.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** The minus sign belongs in FRONT of the unit — a negative amount reads
 *  "-$11.0K", never "$-11.0K". Metrics that are debits (Revenue Adjustments)
 *  are negative for every period, so this is their normal shape. */
export function fmtCurrency(v: number): string {
  const sign = v < 0 ? "-" : "";
  const a = Math.abs(v);
  if (a >= 1e6) return sign + "$" + (a / 1e6).toFixed(2) + "M";
  if (a >= 1e3) return sign + "$" + (a / 1e3).toFixed(1) + "K";
  return sign + "$" + fmtNum(a);
}

export function fmtCount(v: number): string {
  return fmtNum(v);
}

export function fmtVal(metric: Metric, v: number): string {
  switch (metric.kind) {
    case "pct": return fmtNum(v) + "%";
    case "currency": return fmtCurrency(v);
    case "days": return fmtNum(v) + "d";
    case "hours": return fmtNum(v) + "h";
    case "minutes": return fmtNum(v) + "m";
    default: return fmtNum(v);
  }
}

/** Drill-down summary values: same "as is" number rule as {@link fmtVal} (whole
 *  → integer, fractional → 2 truncated decimals) with the metric's unit — e.g.
 *  8.1332 → "8.13%", 1234.5 → "$1,234.50". Used by the By-X summary bars/table. */
export function fmtSummaryVal(metric: Metric, v: number): string {
  const n = fmtNum(v);
  switch (metric.kind) {
    case "pct": return n + "%";
    case "currency": return (v < 0 ? "-$" : "$") + fmtNum(Math.abs(v));
    case "days": return n + "d";
    case "hours": return n + "h";
    case "minutes": return n + "m";
    default: return n;
  }
}

export function formatAbsDelta(metric: Metric, d: Delta): string {
  const sign = d.pct >= 0 ? "+" : "";
  if (metric.kind === "pct") return sign + d.abs.toFixed(1) + "pp";
  return sign + d.pct.toFixed(1) + "%";
}

/* ---------- Axis / unit helpers (compare chart) ---------- */
export function axisFmt(kind: MetricKind): (v: number) => string {
  if (kind === "currency")
    return (v) => {
      const sign = v < 0 ? "-" : "";
      const a = Math.abs(v);
      return a >= 1000 ? sign + "$" + (a / 1000).toFixed(0) + "k" : sign + "$" + Math.round(a);
    };
  if (kind === "pct") return (v) => Math.round(v) + "%";
  if (kind === "days") return (v) => v.toFixed(1);
  return (v) => Math.round(v).toLocaleString();
}
export function unitLabel(kind: MetricKind): string {
  return kind === "currency" ? "$" : kind === "pct" ? "%" : kind === "days" ? "days" : "#";
}
export function unitName(kind: MetricKind): string {
  return kind === "currency" ? "dollars" : kind === "pct" ? "percent" : kind === "days" ? "days" : "counts";
}

/* ---------- Filter option labels ---------- */

/** Tokens that must survive as-is when a raw value is re-cased: clinical
 *  disciplines, org abbreviations and the metric acronyms that show up inside
 *  branch and source names. Extend this rather than special-casing at a call
 *  site. Anything three characters or shorter is kept automatically, so only
 *  LONGER acronyms need listing here — the short words that rule gets wrong go
 *  in {@link FORCE_WORD} instead. */
const KEEP_UPPER = new Set([
  "VNA", "VNACJ", "RWJVN", "OASIS", "LUPA", "PDGM", "ALOS", "COTA",
  "SNF", "HHRG", "PDPM", "CHHA", "LHHA", "ADLS",
]);

/** Short tokens that are ordinary WORDS, not acronyms. Without these the
 *  "three characters or shorter" rule below shouts them — the referral Source
 *  Type list is full of relationships ("SON-IN-LAW", "SON"). */
const FORCE_WORD = new Set(["SON", "LAW", "MOM", "DAD", "SIS", "EX", "MR", "MRS", "DR"]);

/** Connector words that read lower-case inside a longer label ("Son-in-Law",
 *  "Power of Attorney"). Title-cased when they lead the value ("In-Home"). */
const LOWER_WORDS = new Set(["IN", "OF", "AND", "THE", "FOR", "TO", "OR"]);

const capitalize = (tok: string): string =>
  tok.charAt(0).toUpperCase() + tok.slice(1).toLowerCase();

/**
 * A filter value as it should READ in a dropdown. Display only — never feed the
 * result to an API: several filters (Discipline, Source Type, Referral Source,
 * Job Title) send the selected string straight through as their SP parameter,
 * so the raw value has to stay the one held in state.
 *
 * Re-casing is decided PER TOKEN, not per value, because the source data mixes
 * the two within one string ("Sister-IN-LAW"): a token carrying lower-case
 * letters is human text someone curated and is left alone, while a SHOUTED one
 * is re-cased. Within the shouted ones a token is preserved when it holds a
 * digit (branch codes like "A01", "M0102"), is an acronym three characters or
 * shorter ("PT", "SN", "HHA", "POA"), or is listed in {@link KEEP_UPPER} —
 * unless it is a known short word ({@link FORCE_WORD}) or connector
 * ({@link LOWER_WORDS}), which the length rule would otherwise mistake for an
 * acronym.
 *
 * Punctuation and separators are untouched, so "AAMER, AAIZA" → "Aamer, Aaiza",
 * "H-CARE" → "H-Care", "SON-IN-LAW" → "Son-in-Law" and "SIGNIFICANT OTHER" →
 * "Significant Other", while "VNA-01-COR" and "POA" survive intact.
 */
export function toDisplayLabel(raw: string): string {
  const s = String(raw ?? "").replace(/\s+/g, " ").trim();
  if (!s) return s;
  let index = 0;
  return s.replace(/[A-Za-z0-9]+/g, (tok) => {
    const isFirst = index++ === 0;
    // Curated text and code-like tokens keep their exact shape.
    if (/[a-z]/.test(tok) || /\d/.test(tok)) return tok;
    const up = tok.toUpperCase();
    if (LOWER_WORDS.has(up)) return isFirst ? capitalize(up) : up.toLowerCase();
    if (FORCE_WORD.has(up)) return capitalize(up);
    if (up.length <= 3 || KEEP_UPPER.has(up)) return tok;
    return capitalize(tok);
  });
}
